import BaseUISystem from '../BaseUISystem';
import Game from 'Game';
import { PopupID } from 'State/UI/PopupID';
import { addDragonbonesResources } from 'Utility/Resource/ResourceHelper';
import { DRAGONBONES_PATH } from 'Constants/Dragonbones';
import { StandardWheelLayout } from 'Layouts/Wheel/StandardWheelLayout';
import { PremiumWheelLayout } from 'Layouts/Wheel/PremiumWheelLayout';
import { StandardWheelRewardLayout } from 'Layouts/Wheel/StandardWheelRewardLayout';
import { PremiumWheelRewardLayout } from 'Layouts/Wheel/PremiumWheelRewardLayout';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import BaseMenu from '../Menu/BaseMenu';
import Armature from 'Prototype/Armature';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import { Anim, AnimFrom, StartAnimationClipAsync } from '../../Animation/AnimationHelper';
import { DragonbonesDefault, DragonbonesDefaultMeta } from '../../Animation/Clip';
import Renderable from 'Component/Renderable';
import TextComponent from 'Component/Render/Text';
import { propsByRewardType } from './wheelPrizes';
import { Easing } from '@tweenjs/tween.js';
import { promisifySignal, sleep } from 'Utility/async';
import Interactable from 'Component/Interactable';
import { applyDefaultButtonAnimation } from '../UIHelper';
import { UIActions } from 'State/UI/UIActions';
import LevelHelper from 'Utility/LevelHelper';
import { boosterTypeByRewardType, IAPReward } from 'Utility/RewardHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { IAPProduct } from 'Utility/SDK/Drivers/IAPCatalog';
import { isShopSupported, Platform } from 'Utility/Platform';
import { Random } from 'Utility/Random';
import TransformComponent from 'Component/Transform';
import SpriteComponent from 'Component/Render/Sprite';
import { PlayerActions } from 'State/Player/PlayerActions';
import { GameScene } from 'State/Game/GameScene';
import { TActionType } from 'Utility/Tracking/DataSet/Transaction';
import Sprite from 'Prototype/Sprite';
import { INITIAL_PREMIUM_SPINS_AMOUNT } from 'Constants/Player';
import Text from 'Prototype/Text';
import { formatWithDecimals } from 'Utility/Format';
import { WheelRotateAnimation } from './WheelRotateAnimation';
import SDKIntegrationsHelper from 'Utility/SDK/SDKIntegrationsHelper';
import { PlaySound, StopSound, getAudioMusic } from '../../Audio/AudioHelper';
import WorldSystem from 'System/World/WorldSystem';
import { getCurrentLevelNumber } from 'State/Player/PlayerLevelHelper';
import { trackWheelResourceEvent } from './WheelTracking';
import { unlockLevelByPreBoosterType } from '../Menu/LevelPopup/LevelPopupBooster';
import { isPreBoosterType } from 'State/Card/BoosterType';
import { report } from 'report';
import { GameActions } from 'State/Game/GameActions';
import { ProductId } from 'Utility/Product';
import { MUSIC_VOLUME_VALUE } from 'Constants/World';
import { IPlayerState } from 'State/Player/IPlayerState';
import { ContextType, ResponseStatus, ResponseType } from 'State/Game/ContextType';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';

const DATA_KEY = 'wheelRewardsJson';

type Prize = {
    type: RewardType;
    amount: number;
    x: number;
    y: number;
    angle?: number;
    scale?: number;
};

type PrizeWeightData = {
    bg: string;
    prizes: Prize[];
};

type Data = {
    standardPrizeWeights: Random.IWeightedItem<PrizeWeightData>[];
    standardMultiplierWeights: Random.IWeightedItem<number>[];
    premiumPrizeWeights: Random.IWeightedItem<PrizeWeightData>[];
    premiumMultiplierWeights: Random.IWeightedItem<number>[];
    overridesForLockedReward: Record<RewardType, Prize>;
};

type Reward = {
    layout: any;
    glowArmature: Armature;
};

type WheelPopupProps = {
    premium?: boolean;
};

export interface ILuckyWheelShareData {
    type: string;
    prizes: {
        type: RewardType;
        amount: number;
        props: {
            asset: string;
            wheelAsset?: string;
            width?: number;
            scale?: number;
        };
    }[];
}

export default class WheelPopup extends BaseMenu {
    private layout: any;
    private rewards: Reward[];
    private idleParticlesArmature: Armature;
    private winArmature: Armature;
    private baseArmature: Armature;
    private titleRaysArmature: Armature | null = null;
    private titleArmature: Armature;
    private playButtonGlareArmature: Armature;
    private claimButtonGlareArmature: Armature;
    private sectorLightUpArmature: Armature;
    private sectorParticleArmature: Armature;
    private premium: boolean;

    private handlingOnStopClick: boolean = false;

    /**
     * Only used for handling unconsumed premium spin purchases. Should only be `true` when returning to the game and
     * automatically consuming & rewarding a premium spin.
     * @private
     */
    private premiumFree: boolean;
    private actionType: TActionType;

    private prizeIndex: number;
    private multiplierIndex: number;
    private prizes: { type: RewardType; amount: number }[];
    private multiplier: number;

    private bigWheelRotateAnimation: WheelRotateAnimation;
    private smallWheelRotateAnimation: WheelRotateAnimation;

    private static _spinProduct: IAPProduct = null;
    private static _spinReward: IAPReward = null;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.WHEEL, uiSystem, false);

        this.clipShow = 'AnimationClip';
    }

    public async preload(): Promise<void> {
        Game.resource.add('wheel', 'resource/graphics/sprite_sheets/wheel.json');
        Game.resource.add(DATA_KEY, 'resource/data/rewards/wheelRewards.json');
        addDragonbonesResources('spinwheel_standart', `${DRAGONBONES_PATH}/WheelSpin/`);
        addDragonbonesResources('spinwheel_premium', `${DRAGONBONES_PATH}/PremiumWheel/`);

        super.preload();
    }

    public onShow(props: WheelPopupProps): void {
        super.onShow(props);

        const playerState = Game.store.getState().playerState;
        const currentLevel = getCurrentLevelNumber(playerState);
        this.premium = props.premium ?? false;
        this.rewards = [];
        this.handlingOnStopClick = false;

        Game.scene
            .getActiveScene()
            .getSystem(WorldSystem)
            ?.cancelWheelPopupTasks();

        if (!this.premium && currentLevel === playerState.lastClaimedSpinLevel) {
            this.handleIllegalSpin();
            return;
        }

        WheelPopup.tryToResetPremiumSpinsAmount();

        if (this.premium) {
            PlaySound('Premium_Wheel_Appear');
        } else {
            PlaySound('Standard_ Wheel_Appear');
        }

        const Layout = this.premium ? PremiumWheelLayout : StandardWheelLayout;
        this.layout = new Layout();
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        const { multiplierWeights, prizeWeights, levelEntry } = this.getRewardsData();

        prizeWeights.forEach((weight, i) => {
            const bg = GameObject.Instantiate(Sprite);
            const bgSprite = bg.getComponent(SpriteComponent);
            this.layout.sectorsContainer.addChild(bg);
            bg.getComponent(TransformComponent).rotation = ((Math.PI * 2) / prizeWeights.length) * i;
            bgSprite.anchor.set(0.5, 1);
            bgSprite.texture = weight.data.bg;

            weight.data.prizes.forEach(item => {
                const props = propsByRewardType[item.type];
                const sprite = GameObject.Instantiate(Sprite, { texture: props.wheelAsset ?? props.asset });
                bg.addChild(sprite);

                const transform = sprite.getComponent(TransformComponent);
                sprite.getComponent(SpriteComponent).anchor.set(0.5);
                transform.x = item.x;
                transform.y = item.y + 10;
                transform.rotation = ((item.angle ?? 0) / 180) * Math.PI;
                transform.scale.set(item.scale ?? 1);

                const amount = GameObject.Instantiate(Text, {
                    text:
                        item.type === RewardType.COINS
                            ? `${formatWithDecimals(item.amount * levelEntry)}`
                            : `${item.amount}`,
                    position: new Point(transform.x, transform.y - 53),
                    anchor: new Point(0.5, 0.5),
                    style: {
                        fill: '#ffffff',
                        fontFamily: 'FrancoisOne',
                        fontSize: 32,
                        strokeThickness: 6,
                        stroke: 'rgba(0, 0, 0, 0.56)',
                    },
                });
                bg.addChild(amount);
            });
        });

        if (this.premium) {
            const coinRewardText: TextComponent = this.layout.coinRewardText.getComponent(TextComponent);
            const highestMult: number = multiplierWeights.reduce((e, p) => {
                if (e.data > p.data) {
                    return e;
                } else {
                    return p;
                }
            }).data;
            const levelCost = LevelHelper.getLevelCost(Game.player.getHighestLevelCompleted());
            const multiplier = highestMult || 10;
            const coinsValue = levelCost * 25 * multiplier;

            const language = Game.sdk.getLanguage();
            const fullValue = coinsValue.toLocaleString(language, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
            });

            coinRewardText.text = coinRewardText.text.replace('[X]', fullValue);

            const coinTextTransform = this.layout.coinRewardText.getComponent(TransformComponent);
            const coinSpriteTransform = this.layout.coinSprite.getComponent(TransformComponent);

            coinSpriteTransform.x = -(coinTextTransform.width / 2) - coinSpriteTransform.width / 2;
        }

        this.layout.multiplierText.getComponent(TextComponent).style.fill = this.premium
            ? ['#6AACE6', '#ffffff', '#6AACE6', '#ffffff', '#6AACE6']
            : ['#FFFFEE', '#FFFFEE', '#F0C703', '#F0C703', '#FEE715'];

        this.layout.multiplierText.getComponent(TextComponent).style.fillGradientStops = this.premium
            ? [0, 0.35, 0.55, 0.7, 1]
            : [0, 0.5, 0.5, 0.6, 1];

        this.layout.multipliers.forEach((multiplier: any, i: number) => {
            multiplier.getComponent(TextComponent).text = `x${multiplierWeights[i].data}`;
        });

        this.createArmatures();
        this.setupButtons();
        this.reset();
        this.playAnimations();
    }

    private reset(animate: boolean = false): void {
        const { levelEntry, multiplierWeights, prizeWeights } = this.getRewardsData();
        const state = Game.store.getState();

        this.premiumFree = this.premium && state.playerState.inventory[RewardType.PREMIUM_SPIN] > 0;
        this.rewards.forEach(reward => {
            reward.layout.root.destroy();
            reward.glowArmature.destroy();
        });
        this.rewards = [];
        this.actionType = this.getActionType();

        this.prizeIndex = Random.getRandomWeightedIndex(prizeWeights);
        this.prizes = prizeWeights[this.prizeIndex].data.prizes.map(it => ({
            ...it,
            amount: it.amount * (it.type === RewardType.COINS ? levelEntry : 1),
        }));

        this.multiplierIndex = Random.getRandomWeightedIndex(multiplierWeights);
        this.multiplier = multiplierWeights[this.multiplierIndex].data;
        if ((this.premium && !state.playerState.spunFirstDiamondWheel) || !state.playerState.spunFirstNormalWheel) {
            const minMultiplier = this.premium ? 5 : 3;
            while (this.multiplier !== minMultiplier) {
                this.multiplierIndex = Random.getRandomWeightedIndex(multiplierWeights);
                this.multiplier = multiplierWeights[this.multiplierIndex].data;
            }
        }

        this.layout.spinButton.getComponent(Interactable).enabled = true;
        this.layout.stopButton.getComponent(Interactable).enabled = false;
        this.layout.claimButton.getComponent(Interactable).enabled = false;

        if (this.premium) {
            this.layout.closeButton.getComponent(Interactable).enabled = true;

            this.layout.availableAmount.getComponent(
                TextComponent
            ).text = `${WheelPopup.getPremiumSpinsAmount()}x ${Game.locale.getText('Available')}`;

            this.layout.spinText.getComponent(TextComponent).text = this.premiumFree
                ? `${Game.locale.getText('Free')} x${state.playerState.inventory[RewardType.PREMIUM_SPIN]}`
                : WheelPopup.getSpinProduct()!.price;
        }

        [
            this.layout.haloWinContainer,
            this.layout.sectorLightUpContainer,
            this.layout.sectorParticleContainer,
            this.layout.sharePanel,
            this.layout.multiplierPanel,
            this.layout.stopButtonContainer,
        ].forEach(animate ? this.hideObject : this.hideObjectSync);

        const objectsToShow: any[] = [];
        if (this.premium) {
            objectsToShow.push(this.layout.closeButton);
            if (WheelPopup.getPremiumSpinsAmount() < INITIAL_PREMIUM_SPINS_AMOUNT) {
                objectsToShow.push(this.layout.availableAmount);
            }
        }

        [this.layout.spinPanel, ...objectsToShow].forEach(this.showObject);

        this.layout.spinButtonContainer.getComponent(TransformComponent).scale.set(1);
        this.play(this.baseArmature, { animationName: 'idle' });

        sleep(animate ? 500 : 0).then(() => {
            this.createRewardLayouts();
        });

        this.bigWheelRotateAnimation = new WheelRotateAnimation({
            wheel: this.layout.wheelPrizes,
            sectorsAmount: prizeWeights.length,
            prizeIndex: this.prizeIndex,
            side: 1,
            speed: 8,
            pointer: this.layout.bigWheelPointer,
            cleanupManager: this.cleanupManager,
        });

        this.smallWheelRotateAnimation = new WheelRotateAnimation({
            wheel: this.layout.wheelMultipliers,
            sectorsAmount: multiplierWeights.length,
            prizeIndex: this.multiplierIndex,
            side: -1,
            speed: 8,
            pointer: this.layout.smallWheelPointer,
            cleanupManager: this.cleanupManager,
        });
    }

    private createRewardLayouts(): void {
        let x = 0;

        this.prizes.forEach(prize => {
            const Layout = this.premium ? PremiumWheelRewardLayout : StandardWheelRewardLayout;
            const rewardLayout = new Layout();
            const rewardTransform = rewardLayout.reward.getComponent(TransformComponent);
            const amountTransform = rewardLayout.amount.getComponent(TransformComponent);
            const props = propsByRewardType[prize.type];
            rewardTransform.scale.set(props.scale ?? 1);

            const rewardWidth = props.width ?? rewardTransform.width;
            rewardLayout.reward.getComponent(SpriteComponent).texture = props.asset;
            rewardLayout.amount.getComponent(TextComponent).text = formatWithDecimals(
                prize.amount * this.multiplier,
                ' '
            );

            rewardTransform.x = x + rewardWidth / 2;
            amountTransform.x = x + rewardWidth + 10 + amountTransform.width / 2;
            x = amountTransform.x + amountTransform.width / 2 + 40;

            const glowArmature = this.addArmature({
                armatureName: 'reward_glow',
                parent: rewardLayout.root,
            });
            glowArmature
                .getComponent(TransformComponent)
                .position.copy(rewardLayout.reward.getComponent(TransformComponent).position);
            glowArmature.getComponent(Renderable).visible = false;

            this.layout.rewardContainer.addChild(rewardLayout.root);
            this.rewards.push({ layout: rewardLayout, glowArmature });
        });

        const rewardContainerTransform = this.layout.rewardContainer.getComponent(TransformComponent);
        rewardContainerTransform.pivot.x = (x - 40) / 2;
    }

    private createArmatures(): void {
        this.idleParticlesArmature = this.addArmature({
            armatureName: 'wheel_halo',
            parent: this.layout.haloContainer,
        });

        this.winArmature = this.addArmature({
            armatureName: 'wheel_halo_win',
            parent: this.layout.haloWinContainer,
        });

        this.baseArmature = this.addArmature({
            armatureName: 'wheel_base',
            parent: this.layout.wheelBaseContainer,
        });

        if (this.premium) {
            this.titleRaysArmature = this.addArmature({
                armatureName: 'title_rays',
                parent: this.layout.titleRaysContainer,
            });
        }

        this.titleArmature = this.addArmature({
            armatureName: 'title',
            parent: this.layout.titleContainer,
        });

        this.playButtonGlareArmature = this.addArmature({
            armatureName: this.premium ? 'buy_button_glare' : 'play_button_glare',
            parent: this.layout.spinButtonContainer,
            offset: { x: 0, y: 2 },
        });

        this.claimButtonGlareArmature = this.addArmature({
            armatureName: this.premium ? 'share_button_glare' : 'play_button_glare',
            parent: this.layout.claimButtonContainer,
            offset: { x: 0, y: 2 },
        });
        const shareTransform = this.claimButtonGlareArmature.getComponent(TransformComponent);
        shareTransform.scale.set(0.825);
        shareTransform.y = 3;

        this.sectorLightUpArmature = this.addArmature({
            armatureName: 'win_sector_light_up',
            parent: this.layout.sectorLightUpContainer,
        });

        this.sectorParticleArmature = this.addArmature({
            armatureName: 'win_sector_particle',
            parent: this.layout.sectorParticleContainer,
        });
    }

    private setupButtons(): void {
        this.addEventListener(this.layout.spinButton, GameObjectEvent.SELECTED, this.handleSpinClick);
        this.addEventListener(this.layout.stopButton, GameObjectEvent.SELECTED, this.handleStopClick);
        this.addEventListener(this.layout.claimButton, GameObjectEvent.SELECTED, this.handleClaimClick);
        this.layout.stopButton.getComponent(Interactable).tintOnDisable = true;

        [this.layout.spinButton, this.layout.stopButton].forEach(b => {
            applyDefaultButtonAnimation(b, 'Wheel_Button');
        });
        [this.layout.claimButton].forEach(b => {
            applyDefaultButtonAnimation(b);
        });

        if (this.premium) {
            this.addEventListener(this.layout.closeButton, GameObjectEvent.SELECTED, this.handleCloseClick);
            applyDefaultButtonAnimation(this.layout.closeButton);
        }
    }

    private playAnimations(): void {
        const { wheelContainer, panelContainer, titleContainer } = this.layout;

        this.element.getComponent(Renderable).visible = true;

        AnimFrom(wheelContainer, { scaleX: 0, scaleY: 0, duration: 500, easing: Easing.Back.Out });
        AnimFrom(panelContainer, { scaleX: 0, scaleY: 0, duration: 500, easing: Easing.Back.Out, delay: 200 });
        AnimFrom(titleContainer, { scaleX: 0, scaleY: 0, duration: 500, easing: Easing.Back.Out, delay: 300 });

        if (this.premium) {
            if (getAudioMusic()) {
                const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
                worldSystem.setMusicVolume(getAudioMusic());
            }
            PlaySound('Premium_Wheel_Idle', {
                loop: true,
            });
        }
        this.play(this.idleParticlesArmature, { animationName: 'idle' });
        this.play(this.baseArmature, { animationName: 'idle' }); // idle win
        this.play(this.titleArmature, { animationName: 'idle' });
        this.play(this.playButtonGlareArmature, { animationName: 'idle' });
        this.play(this.claimButtonGlareArmature, { animationName: 'idle' });
        if (this.titleRaysArmature) this.play(this.titleRaysArmature, { animationName: 'idle' });
    }

    private addArmature(props: {
        armatureName: string;
        parent: GameObject;
        offset?: { x: number; y: number };
    }): Armature {
        const armature = GameObject.Instantiate(Armature, {
            dragonbonesName: this.premium ? 'spinwheel_premium' : 'spinwheel_standart',
            armatureName: props.armatureName,
            playOnStart: false,
        });
        props.parent.addChild(armature);

        if (props.offset) {
            const trans: TransformComponent = armature.getComponent(TransformComponent);
            if (trans) {
                trans.position.set(trans.position.x + props.offset.x, trans.position.y + props.offset.y);
            }
        }

        return armature;
    }

    private play(armature: Armature, props: DragonbonesDefaultMeta): Promise<void> {
        return StartAnimationClipAsync(new DragonbonesDefault(armature, props));
    }

    private handleIllegalSpin(): void {
        report('illegal wheel spin', { once: true });
        // Why load world, could get stuck in a loop where the world loads and this popup auto opens and loads world again
        GameActions.loadWorld();
    }

    /**
     * TODO: The dispatches in this method should not be deterministic. All of the validation and state changes could be
     ** consolidated into a single Redux action.
     */
    private readonly handleSpinClick = async (): Promise<void> => {
        const state = Game.store.getState();
        const currentLevel = getCurrentLevelNumber(state.playerState);

        if (this.premium) {
            // If resolving an unconsumed purchase we must subtract a premium_spin from the player's inventory.
            if (this.premiumFree) {
                const state = Game.store.getState(); // Make sure to get the latest state.
                Game.store.dispatch(
                    PlayerActions.setPlayerState({
                        ...state.playerState,
                        inventory: {
                            ...state.playerState.inventory,
                            [RewardType.PREMIUM_SPIN]: state.playerState.inventory[RewardType.PREMIUM_SPIN] - 1,
                        },
                        // Decrement remaining Premium-spin count for free premium spins as well.
                        premiumWheelSpinsAmount: Math.max(0, state.playerState.premiumWheelSpinsAmount - 1),
                    })
                );
            } else {
                const productReward = WheelPopup.getSpinProductReward();

                GameActions.setContext(ContextType.PURCHASE_PRODUCT, {
                    product: productReward,
                    actionType: this.actionType,
                });

                // Wait for context to be restored
                while (true) {
                    const result = await Game.store.waitFor(GameActions.restoreContext);

                    // If not the correct response then continue to wait
                    if (
                        result.payload.type !== ResponseType.PURCHASE_PRODUCT ||
                        result.payload.data.product !== productReward
                    )
                        continue;

                    // TODO: Should this do more than return? Error message?
                    if (result.payload.status === ResponseStatus.FAIL) {
                        return;
                    } else {
                        // Product successfully purchased; break from loop
                        break;
                    }
                }
            }
        } else if (currentLevel !== state.playerState.lastClaimedSpinLevel) {
            // Note that the state reference here is retrieved before the condition starts.
            const system = Game.scene.getActiveScene().getSystem(WorldSystem);
            const wheel = system.worldMap.luckyWheelContainer.findChildByName(`luckyWheel_${currentLevel}`);

            if (wheel) {
                // to mitigate multi-spins
                wheel.getComponent(Interactable).enabled = false;

                Anim(wheel, {
                    alpha: 0,
                    duration: 500,
                    easing: Easing.Sinusoidal.Out,
                }).then(() => {
                    if (wheel.destroyed) return;

                    wheel.addComponent(Renderable);
                    wheel.getComponent(Renderable).visible = false;
                });
            }
        }

        const { spinButtonContainer, stopButtonContainer, stopButton } = this.layout;
        this.layout.spinButton.getComponent(Interactable).enabled = false;
        this.giveRewards();

        PlaySound('Wheel_Start');
        PlaySound('Wheel_Spinning', {
            loop: true,
        });

        if (this.premium) {
            this.layout.closeButton.getComponent(Interactable).enabled = false;

            [this.layout.closeButton, this.layout.availableAmount].forEach(this.hideObject);
        }

        this.bigWheelRotateAnimation.start();
        this.smallWheelRotateAnimation.start();

        await promisifySignal(this.bigWheelRotateAnimation.onConstSpeedStart);

        stopButtonContainer.getComponent(Renderable).visible = true;
        stopButtonContainer.getComponent(Renderable).alpha = 1;

        AnimFrom(stopButtonContainer, { scaleX: 0, scaleY: 0, duration: 500, easing: Easing.Back.Out, delay: 300 });
        await Anim(spinButtonContainer, { scaleX: 0, scaleY: 0, duration: 500, easing: Easing.Back.In });

        stopButton.getComponent(Interactable).enabled = true;
    };

    private giveRewards(): void {
        const rewards = this.prizes.map(p => ({ type: p.type, value: p.amount * this.multiplier }));

        PlayerActions.receiveRewards({
            rewards,
            queueTask: Game.store.getState().activeScene.result === GameScene.WORLD,
            rewardFrom: TActionType.WHEEL_SPIN,
        });

        trackWheelResourceEvent({ rewards, tActionType: this.actionType });

        PlayerActions.spinWheel(this.premium);
    }

    private readonly handleStopClick = async (): Promise<void> => {
        if (this.handlingOnStopClick) {
            console.warn("Can't stop wheel; already stopping.");
            return;
        }
        this.handlingOnStopClick = true;
        try {
            const { shareButtonContainer, claimButtonContainer } = this.layout;
            this.layout.stopButton.getComponent(Interactable).enabled = false;
            this.layout.claimButton.getComponent(Interactable).enabled = true;
            this.layout.multiplierText.getComponent(TextComponent).text = `X${this.multiplier}`;

            PlaySound('Wheel_Stop');
            StopSound('Wheel_Spinning');

            this.bigWheelRotateAnimation.stop();

            this.bigWheelRotateAnimation.onLapLeft.addListenerOnce(async () => {
                await sleep(3000);
                this.smallWheelRotateAnimation.stop();
            });

            await promisifySignal(this.smallWheelRotateAnimation.onComplete);

            [
                this.layout.haloWinContainer,
                this.layout.sectorLightUpContainer,
                this.layout.sectorParticleContainer,
                this.layout.multiplierPanel,
                this.layout.sharePanel,
            ].forEach(this.showObject);

            [this.layout.spinPanel].forEach(this.hideObject);

            PlaySound('Wheel_Reward');
            this.play(this.winArmature, { animationName: 'win', playTimes: 1 });
            this.play(this.sectorLightUpArmature, { animationName: 'animation', playTimes: 1 });
            this.play(this.sectorParticleArmature, { animationName: 'idle' });
            this.play(this.baseArmature, { animationName: 'win', playTimes: 1 }).then(() => {
                this.play(this.baseArmature, { animationName: 'win_idle' });
            });

            if (Game.config.platform !== Platform.ANDROID && Game.config.platform !== Platform.IOS) {
                AnimFrom(claimButtonContainer, {
                    x: 100,
                    alpha: 0,
                    duration: 300,
                    easing: Easing.Sinusoidal.Out,
                    delay: 1000,
                });
            } else {
                claimButtonContainer.getComponent(TransformComponent).x = 0;
            }

            this.rewards.map(async reward => {
                const { layout } = reward;
                AnimFrom(layout.reward, { scaleX: 0, scaleY: 0, easing: Easing.Back.Out, duration: 500, delay: 500 });
                AnimFrom(layout.amount, { alpha: 0, duration: 500, delay: 500 });

                await sleep(500);

                this.play(reward.glowArmature, { animationName: 'appear', playTimes: 1 });
                await sleep(50); // fix glowArmature blink on start

                // Renderable seems to be undefined usually after "Error: Unable to load advertisement." errors
                if (!reward.glowArmature.destroyed)
                    (reward.glowArmature.getComponent(Renderable) || { visible: false }).visible = true;
            });
        } catch (error) {
            report('Failed to stop spin wheel.', {
                error,
                once: true,
                sampleRate: 0.1,
                tags: {
                    premium: this.premium,
                },
                fingerprint: 'SpinWheelPopup:handleStopClick:FailedToStop',
            });
        }
        this.handlingOnStopClick = false;
    };

    private readonly handleClaimClick = (): void => {
        const showNext = isShopSupported() && WheelPopup.getSpinProduct() && WheelPopup.getPremiumSpinsAmount() > 0;

        if (showNext && this.premium) {
            this.reset(true);
            return;
        }

        if (showNext) {
            UIActions.queuePopup(PopupID.WHEEL, { premium: true });
        }

        UIActions.hidePopup(PopupID.WHEEL);
    };

    private readonly handleShareClick = (): void => {
        const shareData: ILuckyWheelShareData = {
            type: this.premium ? 'premium' : 'standart',
            prizes: this.prizes.map(p => ({
                type: p.type,
                amount: p.amount * this.multiplier,
                props: propsByRewardType[p.type],
            })),
        };

        SDKIntegrationsHelper.shareLuckyWheelRewards(shareData, this.premium);
    };

    private readonly handleCloseClick = (): void => {
        if (this.premium) {
            const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
            worldSystem.setMusicVolume(getAudioMusic());
            StopSound('Premium_Wheel_Idle');
        }

        if (WheelPopup.getPremiumSpinsAmount() === INITIAL_PREMIUM_SPINS_AMOUNT) {
            UIActions.queuePopup(PopupID.WHEEL_QUIT_CONFIRM, {});
        }

        UIActions.hidePopup(PopupID.WHEEL);
    };

    private addEventListener(object: GameObject, event: GameObjectEvent, listener: (...args: unknown[]) => any): void {
        object.on(event, listener);

        this.cleanupManager.add(() => {
            object.off(event, listener);
        });
    }

    private getRewardsData(): {
        levelEntry: number;
        prizeWeights: Random.IWeightedItem<PrizeWeightData>[];
        multiplierWeights: Random.IWeightedItem<number>[];
    } {
        const data: Data = Game.resource.get(DATA_KEY);
        const levelEntry = LevelHelper.getLevelCost(Game.player.getHighestLevelCompleted());
        const multiplierWeights = this.premium ? data.premiumMultiplierWeights : data.standardMultiplierWeights;
        let prizeWeights = this.premium ? data.premiumPrizeWeights : data.standardPrizeWeights;

        const isLocked = (type: RewardType): boolean => {
            const boosterType = boosterTypeByRewardType[type];

            if (isPreBoosterType(boosterType)) {
                return Game.player.getHighestLevelCompleted() + 1 < unlockLevelByPreBoosterType[boosterType];
            }

            return false;
        };

        prizeWeights = prizeWeights.map(weight => {
            return {
                ...weight,
                data: {
                    ...weight.data,
                    prizes: weight.data.prizes.map(p => {
                        const override = data.overridesForLockedReward[p.type];
                        return isLocked(p.type) && override ? override : p;
                    }),
                },
            };
        });

        return { levelEntry, prizeWeights, multiplierWeights };
    }

    private static getSpinProduct(): IAPProduct | undefined {
        return (
            WheelPopup._spinProduct ||
            (WheelPopup._spinProduct = Game.sdk.getCatalog().products.find(e => e.productId === ProductId.PREMIUM_SPIN))
        );
    }

    private static getSpinProductReward(): IAPReward | undefined {
        return (
            WheelPopup._spinReward ||
            (WheelPopup._spinReward = IAPHelper.CACHED_IAP_REWARDS_MAP()[ProductId.PREMIUM_SPIN])
        );
    }

    private static getPremiumSpinsAmount(): number {
        return Game.store.getState().playerState.premiumWheelSpinsAmount;
    }

    private static setPremiumSpinsAmount(playerState: IPlayerState, value: number): void {
        PlayerActions.setPlayerState({
            ...playerState,
            premiumWheelSpinsAmount: Math.max(0, value),
        });
    }

    private static tryToResetPremiumSpinsAmount(): void {
        const state = Game.store.getState();
        const completedLevelNumber = Game.player.getHighestLevelCompleted();

        if (completedLevelNumber > state.playerState.premiumWheelResetLevel) {
            PlayerActions.setPlayerState({
                ...Game.store.getState().playerState,
                premiumWheelResetLevel: completedLevelNumber,
                premiumWheelSpinsAmount: INITIAL_PREMIUM_SPINS_AMOUNT,
            });
        }
    }

    private readonly hideObject = async (gameObject: GameObject): Promise<void> => {
        await Anim(gameObject, { alpha: 0, duration: 500 });
        let renderable = gameObject?.getComponent(Renderable);
        if (gameObject.destroyed || !renderable) return;
        renderable.visible = false;
    };

    private readonly showObject = async (gameObject: GameObject): Promise<void> => {
        gameObject.getComponent(Renderable).visible = true;
        await Anim(gameObject, { alpha: 1, duration: 500 });
    };

    private readonly hideObjectSync = async (gameObject: GameObject): Promise<void> => {
        let renderable = gameObject?.getComponent(Renderable);
        if (gameObject.destroyed || !renderable) return;
        renderable.visible = false;
        renderable.alpha = 0;
    };

    private getActionType(): TActionType {
        const tActionTypes: TActionType[] = [
            TActionType.WHEEL_SPIN_PREMIUM_5,
            TActionType.WHEEL_SPIN_PREMIUM_4,
            TActionType.WHEEL_SPIN_PREMIUM_3,
            TActionType.WHEEL_SPIN_PREMIUM_2,
            TActionType.WHEEL_SPIN_PREMIUM_1,
        ];

        const premiumSpinsAmount = WheelPopup.getPremiumSpinsAmount() - 1;

        return this.premium ? tActionTypes[premiumSpinsAmount] : TActionType.WHEEL_SPIN;
    }
}
